/**
 * SLDSidePanel — data point & advanced rule list panel for placement/removal.
 * Extracted from SLDViewerModal.tsx (no behavior changes).
 */

import type {
  DataPointConfig,
  DataPointRuntimeState,
  AdvancedAlarmRule,
  TriggeredAdvancedRule,
  AlarmState,
} from '@/types/monitoring';
import { stateClass, type PlacementType, type SidePanelTab } from './sldUtils';

export interface SLDSidePanelProps {
  datapoints: DataPointConfig[];
  advancedRules: AdvancedAlarmRule[];
  runtimeStates: Map<string, DataPointRuntimeState>;
  triggered: TriggeredAdvancedRule[];
  placedDpIds: Set<string>;
  placedRuleIds: Set<string>;
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  sidePanelTab: SidePanelTab;
  onSidePanelTabChange: (tab: SidePanelTab) => void;
  onStartPlacement: (itemId: string, type: PlacementType) => void;
  onRemoveFromDiagram: (dpId: string) => void;
  onRemoveRuleFromDiagram: (ruleId: string) => void;
}

export default function SLDSidePanel({
  datapoints,
  advancedRules,
  runtimeStates,
  triggered,
  placedDpIds,
  placedRuleIds,
  searchTerm,
  onSearchTermChange,
  sidePanelTab,
  onSidePanelTabChange,
  onStartPlacement,
  onRemoveFromDiagram,
  onRemoveRuleFromDiagram,
}: SLDSidePanelProps) {
  const filteredDatapoints = datapoints.filter((dp) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return dp.display_name.toLowerCase().includes(s) || dp.entity_id.toLowerCase().includes(s);
  });

  const filteredRules = advancedRules.filter((r) => {
    if (!searchTerm) return true;
    return r.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="sld-side-panel">
      <div className="sld-side-panel-header">
        <h4>Indications</h4>
        <input
          className="sld-side-panel-search"
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
        />
      </div>

      <div className="sld-side-panel-tabs">
        <button
          className={`sld-side-panel-tab${sidePanelTab === 'datapoints' ? ' active' : ''}`}
          onClick={() => onSidePanelTabChange('datapoints')}
        >
          Data Points ({datapoints.length})
        </button>
        <button
          className={`sld-side-panel-tab${sidePanelTab === 'rules' ? ' active' : ''}`}
          onClick={() => onSidePanelTabChange('rules')}
        >
          Advanced Rules ({advancedRules.length})
        </button>
      </div>

      <div className="sld-side-panel-list">
        {sidePanelTab === 'datapoints' && (
          <>
            {filteredDatapoints.length === 0 && (
              <div className="sld-side-panel-empty">
                {datapoints.length === 0
                  ? 'No data points configured. Create them in Monitoring Builder first.'
                  : 'No matches found.'}
              </div>
            )}
            {filteredDatapoints.map((dp) => {
              const runtime = runtimeStates.get(dp.id);
              const state: AlarmState = runtime?.state ?? 'no_data';
              const dpIsPlaced = placedDpIds.has(dp.id);

              return (
                <div key={dp.id} className="sld-side-panel-item">
                  <span className={`sld-side-panel-item__dot ${stateClass(state)}`} />
                  <div className="sld-side-panel-item__info">
                    <div className="sld-side-panel-item__name">{dp.display_name}</div>
                    <div className="sld-side-panel-item__sub">{dp.entity_id}</div>
                  </div>
                  {dpIsPlaced ? (
                    <button
                      className="sld-side-panel-item__action sld-side-panel-item__action--unplace"
                      onClick={() => onRemoveFromDiagram(dp.id)}
                    >
                      Remove
                    </button>
                  ) : (
                    <button
                      className="sld-side-panel-item__action sld-side-panel-item__action--place"
                      onClick={() => onStartPlacement(dp.id, 'datapoint')}
                    >
                      Place
                    </button>
                  )}
                </div>
              );
            })}
          </>
        )}

        {sidePanelTab === 'rules' && (
          <>
            {filteredRules.length === 0 && (
              <div className="sld-side-panel-empty">
                {advancedRules.length === 0
                  ? 'No advanced rules configured.'
                  : 'No matches found.'}
              </div>
            )}
            {filteredRules.map((rule) => {
              const trig = triggered.find((t) => t.rule_id === rule.id);
              const ruleIsPlaced = placedRuleIds.has(rule.id);
              const ruleItemState = trig ? trig.severity : 'ok';

              return (
                <div key={rule.id} className="sld-side-panel-item">
                  <span
                    className={`sld-side-panel-item__dot sld-side-panel-item__dot--diamond ${stateClass(ruleItemState)}`}
                  />
                  <div className="sld-side-panel-item__info">
                    <div className="sld-side-panel-item__name">{rule.name}</div>
                    <div className="sld-side-panel-item__sub">
                      {rule.type.toUpperCase().replace('_', ' ')} &middot; {rule.severity}
                    </div>
                  </div>
                  {ruleIsPlaced ? (
                    <button
                      className="sld-side-panel-item__action sld-side-panel-item__action--unplace"
                      onClick={() => onRemoveRuleFromDiagram(rule.id)}
                    >
                      Remove
                    </button>
                  ) : (
                    <button
                      className="sld-side-panel-item__action sld-side-panel-item__action--place"
                      onClick={() => onStartPlacement(rule.id, 'rule')}
                    >
                      Place
                    </button>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
